import { memo, useState } from 'react';
import { useUnit } from 'effector-react';
import { IconButton } from '@mui/material';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Channels } from '@features/tickets/models/detail-sdui/types';
import { DragNDropAndResizable, ON_CHANGE_REASON, type OnChangeProps } from '@shared/ui';
import { $pending, NEW_OPENED_ID } from '../../../../models/detail-sdui';
import { $openedId } from '../../../../models/detail-sdui-opened-id';
import { ChatsBlock } from './chatsBlock';
import { readWindowPosition, writeWindowPosition, type WindowPosition } from './helpers';
import * as Styled from './styled';

const TICKET_CHAT_WINDOW_POSITION_LS_KEY = 'ticketChatWindowPosition';
const DEFAULT_POSITION: WindowPosition = {
  width: 700,
  height: 600,
  left: window.innerWidth - 700 - 10,
  top: window.innerHeight - 600 - 10,
};

const DndResizableContainer = ({ children }: { children?: JSX.Element }) => (
  <Styled.Container elevation={6}>{children}</Styled.Container>
);

const onChangeDragNDropAndResizable = (data: OnChangeProps) => {
  if (data.changed === ON_CHANGE_REASON.TO_FULL_SCREEN) return;

  writeWindowPosition(TICKET_CHAT_WINDOW_POSITION_LS_KEY, data);
};

const ChatsWindow = ({
  id,
  channels,
  pending,
  onClose,
}: {
  id: string;
  channels: Channels;
  pending: boolean;
  onClose: () => void;
}) => {
  const [windowPosition] = useState(() =>
    readWindowPosition(TICKET_CHAT_WINDOW_POSITION_LS_KEY, DEFAULT_POSITION)
  );

  return (
    <DragNDropAndResizable
      id={id}
      isOpened
      container={DndResizableContainer}
      onClose={onClose}
      headerLeft={
        <Styled.Title variant="h6">
          <IconButton
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            aria-label="Delete"
          >
            <DragIndicatorIcon />
          </IconButton>
        </Styled.Title>
      }
      body={<ChatsBlock channels={channels} pending={pending} />}
      width={windowPosition.width}
      height={windowPosition.height}
      left={windowPosition.left}
      top={windowPosition.top}
      resizePaddings={42}
      onChange={onChangeDragNDropAndResizable}
    />
  );
};

export const Chats = memo(({ channels }: { channels: Channels }) => {
  const [openedId, pending] = useUnit([$openedId, $pending]);
  const [opened, setOpened] = useState(false);

  if (!openedId || openedId === NEW_OPENED_ID) return null;

  const open = () => setOpened(true);

  const close = () => setOpened(false);

  return (
    <>
      {opened && (
        <ChatsWindow
          key={`chats-${openedId}`}
          id={`chats-${openedId}`}
          channels={channels}
          pending={pending}
          onClose={close}
        />
      )}

      <IconButton onClick={open} disabled={opened || pending}>
        <ChatOutlinedIcon />
      </IconButton>
    </>
  );
});
